// eg. /app/store/mymodule.ts
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'
import { buildCookie } from '@/modules/cdn/cookie'

import { ApiTaRe140 } from '@/endpoints/ApiTaRe140'
import { ApiTaRe150 } from '@/endpoints/ApiTaRe150'
import { ApiTaRe160 } from '@/endpoints/ApiTaRe160'
import { ApiTaRe170 } from '@/endpoints/ApiTaRe170'
import { ApiTaCt100 } from '@/endpoints/ApiTaCt100'

export namespace MonthlyCheckType {
  /**
   * 質問タイプ
   * 先月の振り返り: 0
   * 今月の目標: 1
   */
  export const questionType = {
    lookBackLastMonth: 0,
    goalThisMonth: 1,
  } as const
  export type QuestionType = typeof questionType[keyof typeof questionType]

  export function convertQuestionTypeToNumber(type: QuestionType): number {
    switch (type) {
      case questionType.lookBackLastMonth:
        return 0
      case questionType.goalThisMonth:
        return 1
      default:
        throw new Error('unexpected type')
    }
  }

  export function convertNumberToQuestionType(num: number): QuestionType {
    switch (num) {
      case 0:
        return questionType.lookBackLastMonth
      case 1:
        return questionType.goalThisMonth
      default:
        throw new Error('unexpected number')
    }
  }
}

// State types
interface questionChoicesInfoObject {
  monthlyCheckAnswerAdviceId: string
  choiceTextDisp: string
  adviceTextDisp: string
}

interface questionInfoObject {
  monthlyCheckQuestionId: string
  questionType: MonthlyCheckType.QuestionType
  questionTextDisp: string
  questionChoicesInfo: questionChoicesInfoObject[]
}

export type MonthlyCheckQuestionState = {
  memNum: string
  grade: number
  fiscalYear: number
  dispMonth: number
  isDispSendButton: boolean
  isUserAnswered: boolean
  questionsInfo: questionInfoObject[]
} | null

export type AnswerInfo = {
  memNum: string
  grade: number
  fiscalYear: number
  dispMonth: number
  answerAdvicesInfo: {
    questionType: MonthlyCheckType.QuestionType
    monthlyCheckAnswerAdviceId: string
  }[]
} | null

interface MonthlyLearningInfoObject {
  subjectId: string
  name: string
  learningProgress: number
}

export type MonthlyLearningRecordState = {
  memNum: string
  grade: number
  fiscalYear: number
  dispMonth: number
  monthlyLearningInfo: MonthlyLearningInfoObject[]
  firstChoiceJuniorHighSchool: string
} | null

export type MonthlyCheckPdf = {
  informationPdfUrl: string
} | null

// State action parameter
interface ActionParamMonthlyCheckId {
  monthlyCheckId: string
}

interface StateActionParamMonthlyCheck {
  monthlyCheckId: string
  answerInfo: {
    questionType: MonthlyCheckType.QuestionType
    monthlyCheckQuestionId: string
    monthlyCheckAnswerAdviceId: string
  }[]
}

interface AkamaiToken {
  akamaiToken: string
}
const getEmptyAkamaiToken = (): AkamaiToken => ({
  akamaiToken: '',
})

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/MonthlyCheck',
})
export default class MonthlyCheck extends VuexModule {
  // State
  private monthlyCheckQuestion_: MonthlyCheckQuestionState = null
  private answerInfo_: AnswerInfo = null
  private monthlyLearningRecord_: MonthlyLearningRecordState = null
  private monthlyCheckPdf_: MonthlyCheckPdf = null
  private akamaiToken_: AkamaiToken = getEmptyAkamaiToken()

  // Getter
  get monthlyCheckQuestion(): MonthlyCheckQuestionState {
    return this.monthlyCheckQuestion_
  }

  get answerInfo(): AnswerInfo {
    return this.answerInfo_
  }

  get monthlyLearningRecord(): MonthlyLearningRecordState {
    return this.monthlyLearningRecord_
  }

  get monthlyCheckPdf(): MonthlyCheckPdf {
    return this.monthlyCheckPdf_
  }

  get akamaiCookie(): (expiresAt?: string) => string {
    return (expiresAt?: string) => buildCookie(this.akamaiToken_.akamaiToken, expiresAt)
  }

  @Mutation
  updateMonthlyCheckQuestions(newState: MonthlyCheckQuestionState) {
    this.monthlyCheckQuestion_ = newState
  }

  @Mutation
  updateAnswerInfo(newState: AnswerInfo) {
    this.answerInfo_ = newState
  }

  @Mutation
  updateMonthlyLearningRecord(newState: MonthlyLearningRecordState) {
    this.monthlyLearningRecord_ = newState
  }

  @Mutation
  updateMonthlyCheckPdf(newState: MonthlyCheckPdf) {
    this.monthlyCheckPdf_ = newState
  }

  @Mutation
  updateAkamaiToken(value: AkamaiToken) {
    this.akamaiToken_ = value
  }

  @Action
  clear() {
    this.context.commit('updateMonthlyCheckQuestions', null)
    this.context.commit('updateAnswerInfo', null)
    this.context.commit('updateMonthlyLearningRecord', null)
    this.context.commit('updateMonthlyCheckPdf', null)
    this.context.commit('updateAkamaiToken', getEmptyAkamaiToken())
  }

  @Action
  async refreshMonthlyCheckQuestions(param: ActionParamMonthlyCheckId) {
    logger.trace('in: refreshMonthlyCheckQuestions')
    const api = new ApiTaRe140.Api()

    // API: API_TA_RE_140
    const result = await api.get<MonthlyCheckQuestionState>(
      {
        query: {
          monthlyCheckId: param.monthlyCheckId,
        },
      },
      // success
      (data, _) => {
        return {
          memNum: data.memNum,
          grade: data.grade,
          fiscalYear: data.fiscalYear,
          dispMonth: data.dispMonth,
          isDispSendButton: data.isDispSendButton,
          isUserAnswered: data.isUserAnswered,
          questionsInfo: data.questionsInfo.map((questionInfo): questionInfoObject => {
            return {
              monthlyCheckQuestionId: questionInfo.monthlyCheckQuestionId,
              questionType: MonthlyCheckType.convertNumberToQuestionType(questionInfo.questionType),
              questionTextDisp: questionInfo.questionTextDisp,
              questionChoicesInfo: questionInfo.questionChoicesInfo.map(
                (questionChoiceInfo): questionChoicesInfoObject => {
                  return {
                    monthlyCheckAnswerAdviceId: questionChoiceInfo.monthlyCheckAnswerAdviceId,
                    choiceTextDisp: questionChoiceInfo.choiceTextDisp,
                    adviceTextDisp: questionChoiceInfo.adviceTextDisp,
                  }
                }
              ),
            }
          }),
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateMonthlyCheckQuestions', result)
  }

  @Action
  async submission(param: StateActionParamMonthlyCheck) {
    logger.trace('in: submission')
    const api = new ApiTaRe150.Api()

    // API: ApiTaNt020
    const result = await api.post<AnswerInfo>(
      {
        query: {
          monthlyCheckId: param.monthlyCheckId,
        },
        body: {
          answerInfo: param.answerInfo.map(info => {
            return {
              questionType: MonthlyCheckType.convertQuestionTypeToNumber(info.questionType),
              monthlyCheckQuestionId: info.monthlyCheckQuestionId,
              monthlyCheckAnswerAdviceId: info.monthlyCheckAnswerAdviceId,
            }
          }),
        },
      },
      // success
      (data, _) => ({
        memNum: data.memNum,
        grade: data.grade,
        fiscalYear: data.fiscalYear,
        dispMonth: data.dispMonth,
        answerAdvicesInfo: data.answerAdvicesInfo.map(elem => {
          return {
            questionType: MonthlyCheckType.convertNumberToQuestionType(elem.questionType),
            monthlyCheckAnswerAdviceId: elem.monthlyCheckAnswerAdviceId,
          }
        }),
      }),
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateAnswerInfo', result)
  }

  @Action
  async refreshMonthlyLearningRecord(param: ActionParamMonthlyCheckId) {
    logger.trace('in: refreshMonthlyLearningRecord')
    const api = new ApiTaRe160.Api()

    const result = await api.get<MonthlyLearningRecordState>(
      {
        query: {
          monthlyCheckId: param.monthlyCheckId,
        },
      },
      // success
      (data, _) => {
        return {
          memNum: data.memNum,
          grade: data.grade,
          fiscalYear: data.fiscalYear,
          dispMonth: data.dispMonth,
          monthlyLearningInfo: data.monthlyLearningInfo.map((info): MonthlyLearningInfoObject => {
            return {
              subjectId: info.subjectId,
              name: info.name,
              learningProgress: info.learningProgress,
            }
          }),
          firstChoiceJuniorHighSchool: data.firstChoiceJuniorHighSchool,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }

    this.context.commit('updateMonthlyLearningRecord', result)
  }

  @Action
  async refreshMonthlyCheckPdf(param: ActionParamMonthlyCheckId) {
    logger.trace('in: refreshMonthlyCheckPdf')
    const api = new ApiTaRe170.Api()

    const result = await api.get<MonthlyCheckPdf>(
      {
        query: {
          monthlyCheckId: param.monthlyCheckId,
        },
      },
      // success
      (data, _) => {
        return {
          informationPdfUrl: data.informationPdfUrl,
        }
      },
      // error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }

    this.context.commit('updateMonthlyCheckPdf', result)
  }

  @Action
  async getAkamaiToken() {
    logger.trace('in: getAkamaiToken')
    const api = new ApiTaCt100.Api()

    // API: ApiTaCt100
    const result = await api.get<AkamaiToken | null>(
      {},
      // success
      (data, _) => {
        return {
          akamaiToken: data.cmsToken,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateAkamaiToken', result)
  }
}
